import {Alert, Box, Button, Chip, CircularProgress, FormControl, IconButton, MenuItem, Popover, Select, Tooltip, Typography} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import React, {useEffect, useRef, useState} from "react";
import {styled, useTheme} from "@mui/material/styles";
import {useMutationAsUser, useQueryAsUser} from "../../clients/graphql/graphql.hooks";
import {useNavigate} from "react-router-dom";
import {
    PORTFOLIO_ACCOUNT_RESTRICTION_OPTIONS,
    PORTFOLIO_ACCOUNT_RESTRICTION_UPDATE,
    PortfolioAccountRestriction,
    whoIsIt,
} from "../../clients/graphql/views/portfolio-queries";
import {formatMoment} from "../../functions/momentFormatter";
import {PortfolioLpoa} from "../../clients/graphql/pages/lpoa-queries";
import {hasActiveLpoa} from "../../utils/lpoaStatus";

interface InformationCardProps {
    bc: string;
}

const InformationCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'bc',
})<InformationCardProps>(({ bc }) => ({
    marginLeft: '.75rem',
    marginTop: '.3rem',
    marginRight: '.75rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    border: `1px solid ${bc}`,
    borderRadius: '5px',
    fontFamily: 'Roboto',
    letterSpacing: '0.05em',
    textAlign: 'left',
    minHeight: '2.5rem',
    alignItems: 'center',
}));

interface PortfolioCrudPanelProps {
    portfolioKey: number;
    userKey: number;
    deletedAt: string;
    lpoaData?: PortfolioLpoa[];
}

export default function PortfolioCrudPanel({portfolioKey, userKey, deletedAt, lpoaData}: PortfolioCrudPanelProps) {

    const goTo = useNavigate();
    const theme = useTheme();
    const {loading, error, data, refetch} = useQueryAsUser(whoIsIt, {variables: {portfolioKey: portfolioKey}, userKey});

    const portfolio = data?.whoisit?.portfolio;
    const currentAccountRestriction = portfolio?.accountRestriction as PortfolioAccountRestriction | undefined;

    const [pendingAccountRestriction, setPendingAccountRestriction] = useState<PortfolioAccountRestriction | "">("");

    useEffect(() => {
        if (currentAccountRestriction) {
            setPendingAccountRestriction(currentAccountRestriction);
        }
    }, [currentAccountRestriction]);

    const [updateAccountRestriction, updateAccountRestrictionState] = useMutationAsUser(PORTFOLIO_ACCOUNT_RESTRICTION_UPDATE, {userKey});

    const editAnchorRef = useRef<HTMLButtonElement | null>(null);
    const [editOpen, setEditOpen] = useState(false);

    const handleAccountRestrictionSave = async () => {
        if (!pendingAccountRestriction || pendingAccountRestriction === currentAccountRestriction) return;
        try {
            await updateAccountRestriction({
                variables: {portfolioKey, accountRestriction: pendingAccountRestriction},
            });
            await refetch();
            setEditOpen(false);
        } catch (err) {
            console.error("Failed to update account restriction:", err);
            alert("Failed to update account restriction. Please try again.");
        }
    };

    const accountRestrictionChipColor = (value: PortfolioAccountRestriction | undefined): "success" | "error" | "warning" | "default" => {
        switch (value) {
            case "UNRESTRICTED": return "success";
            case "RESTRICTED":
            case "RESTRICTED_ONLY_LIQUIDATION":
            case "RESTRICTED_WITH_RESOLUTION":
                return "error";
            case "UNKNOWN_RESTRICTED": return "warning";
            default: return "default";
        }
    };

    if (loading) {
        return <CircularProgress sx={{ marginLeft: 'calc(50% - 10px)', marginTop: '1rem' }} />;
    }

    if (error) {
        return <Alert severity="warning">{error?.message}</Alert>;
    }
    const curatedPortfolioData = portfolio?.financialInfo;
    const curatedInstitutionData = data?.whoisit?.institution;
    const accountRestrictionDirty = !!pendingAccountRestriction && pendingAccountRestriction !== currentAccountRestriction;

    const borderColor = theme.palette.mode === 'dark' ? '#666' : '#ccc'

    return(
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingBottom: '.75rem'
            // border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#666' : '#ccc'}`,
            }}>
            <Popover
                open={editOpen}
                anchorEl={editAnchorRef.current}
                onClose={() => setEditOpen(false)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <Box sx={{p: 2, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 1}}>
                    <Typography variant="subtitle2">Update Account Restriction</Typography>
                    <FormControl size="small" fullWidth>
                        <Select
                            value={pendingAccountRestriction}
                            displayEmpty
                            onChange={(e) => setPendingAccountRestriction(e.target.value as PortfolioAccountRestriction)}
                            disabled={updateAccountRestrictionState.loading}
                        >
                            {PORTFOLIO_ACCOUNT_RESTRICTION_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1}}>
                        <Button size="small" onClick={() => setEditOpen(false)} disabled={updateAccountRestrictionState.loading}>
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={!accountRestrictionDirty || updateAccountRestrictionState.loading}
                            onClick={handleAccountRestrictionSave}
                        >
                            {updateAccountRestrictionState.loading ? 'Saving…' : 'Save'}
                        </Button>
                    </Box>
                    {updateAccountRestrictionState.error && (
                        <Alert severity="warning">{updateAccountRestrictionState.error.message}</Alert>
                    )}
                    <Typography variant="caption" sx={{color: '#6D6D6D'}}>
                        Last changed: {portfolio?.accountRestrictionChangedAt ? formatMoment(portfolio.accountRestrictionChangedAt) : 'N/A'}
                    </Typography>
                </Box>
            </Popover>
            <Typography variant="h6" component='div' sx={{
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '12px',
                paddingTop: '.75rem',
                paddingLeft: '.75rem'
            }}>
                TOTAL VALUE
            </Typography>

            <Typography variant="h6" component='div' sx={{
                fontFamily: 'Roboto Mono',
                fontSize: '32px',
                fontWeight: 400,
                lineHeight: '42.69px',
                color: (theme) => theme.palette.text.primary,
                paddingLeft: '.75rem',
                paddingTop: '.75rem',
            }}>
                {curatedPortfolioData?.totalValue?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </Typography>

            <Typography variant="h6" component='div' sx={{
                fontFamily: 'Roboto',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '12px',
                letterSpacing: '0.05em',
                paddingTop: '1.5rem',
                paddingLeft: '.75rem'
            }}>
                BUYING POWER
            </Typography>

            <Typography variant="h6" component='div' sx={{
                fontFamily: 'Roboto Mono',
                fontSize: '32px',
                fontWeight: 400,
                lineHeight: '42.69px',
                color: (theme) => theme.palette.text.primary,
                paddingLeft: '.75rem',
                paddingTop: '.75rem',

            }}>
                {curatedPortfolioData?.buyingPower?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between',  }}>
                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '12px',
                    color: '#6D6D6D',
                    paddingTop: '1rem',
                    paddingLeft: '.75rem',
                }}>
                    LAST CHECKED:
                </Typography>

                <Typography variant="h6" component='div' sx={{
                    fontSize: '13px',
                    fontWeight: 200,
                    lineHeight: '12px',
                    color: (theme) => theme.palette.text.primary,
                    paddingTop: '1rem',
                    paddingLeft: '.75rem',
                    paddingRight: '.5rem'
                }}>
                    {formatMoment(curatedPortfolioData?.lastHardCheckAt)}
                </Typography>
            </Box>

            <Typography variant="h6" component='div' sx={{
                fontFamily: 'Roboto',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '12px',
                letterSpacing: '0.05em',
                paddingTop: '1.5rem',
                paddingLeft: '.75rem'
            }}>
                INFORMATION
            </Typography>

            <InformationCard bc={borderColor}>
                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '18px',
                    fontWeight: 400,
                    lineHeight: '21.34px',
                    paddingLeft: '.75rem',
                }}>
                    Restriction
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, margin: '.25rem .5rem' }}>
                    <Tooltip title={portfolio?.accountRestrictionChangedAt ? `Changed: ${formatMoment(portfolio.accountRestrictionChangedAt)}` : 'No change history'}>
                        <Chip
                            label={currentAccountRestriction ?? 'UNKNOWN'}
                            color={accountRestrictionChipColor(currentAccountRestriction)}
                            size="small"
                            sx={{fontWeight: 600, maxWidth: '11rem', '& .MuiChip-label': {textOverflow: 'ellipsis', overflow: 'hidden'}}}
                        />
                    </Tooltip>
                    <IconButton
                        ref={editAnchorRef}
                        size="small"
                        onClick={() => setEditOpen(true)}
                        title="Edit Account Restriction"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Box>
            </InformationCard>

            <InformationCard bc={borderColor}>
                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '18px',
                    fontWeight: 400,
                    lineHeight: '21.34px',
                    paddingLeft: '.75rem',
                }}>
                    User
                </Typography>

                <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    sx={{
                        margin: '.5rem'
                    }}
                    onClick={(e) => {
                        if (e.metaKey || e.ctrlKey) {
                            window.open(`/users/${userKey}`);
                        } else {
                            goTo(`/users/${userKey}`)}
                        }
                }
                >
                    #{userKey}
                </Button>
            </InformationCard>

            <InformationCard bc={borderColor}>
                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '18px',
                    fontWeight: 400,
                    lineHeight: '21.34px',
                    paddingLeft: '.75rem',
                }}>
                    Status
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, margin: '.5rem' }}>
                    {data?.whoisit?.portfolio?.connectionStatus === 'CONNECTED' && (
                        <Chip label="Connected" color="success" />
                    )}

                    {data?.whoisit?.portfolio?.connectionStatus === 'DISCONNECTED' && (
                        <Chip label="Disconnected" color="error" />
                    )}
                    
                    {hasActiveLpoa(lpoaData) && (
                        <Chip
                            label="LPOA"
                            size="small"
                            sx={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: '24px',
                                '& .MuiChip-label': {
                                    px: 1,
                                },
                            }}
                        />
                    )}
                </Box>
            </InformationCard>

            <InformationCard bc={borderColor}>
                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '18px',
                    fontWeight: 400,
                    lineHeight: '21.34px',
                    paddingLeft: '.75rem',
                }}>
                    Account ID
                </Typography>

                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 200,
                    lineHeight: '18px',
                    paddingRight: '.5rem',
                }}>
                    {data?.whoisit?.portfolio?.accountId || 'N/A'}
                </Typography>
            </InformationCard>

            <InformationCard bc={borderColor}>
                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '18px',
                    fontWeight: 400,
                    lineHeight: '21.34px',
                    paddingLeft: '.75rem',
                }}>
                    Institution Key
                </Typography>

                <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    sx={{
                        margin: '.5rem'
                    }}
                    onClick={(e) => {
                        if (e.metaKey || e.ctrlKey) {
                            window.open(`/institution/${curatedInstitutionData?.institutionKey}`);
                        } else {
                            goTo(`/institution/${curatedInstitutionData?.institutionKey}`)}
                    }}>
                    #{curatedInstitutionData?.institutionKey}
                </Button>
            </InformationCard>

            <InformationCard bc={borderColor}>
                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '18px',
                    fontWeight: 400,
                    lineHeight: '21.34px',
                    paddingLeft: '.75rem',
                }}>
                    Created At
                </Typography>

                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 200,
                    lineHeight: '18px',
                    paddingRight: '.5rem',
                }}>
                    {formatMoment(curatedInstitutionData?.createdAt)}
                </Typography>
            </InformationCard>

            <InformationCard bc={borderColor}>
                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '18px',
                    fontWeight: 400,
                    lineHeight: '21.34px',
                    paddingLeft: '.75rem',
                }}>
                    Deleted At
                </Typography>

                <Typography variant="h6" component='div' sx={{
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 200,
                    lineHeight: '18px',
                    paddingRight: '.5rem',
                }}>
                    {deletedAt === null ? 'N/A' : deletedAt}
                </Typography>
            </InformationCard>

        </Box>
    )
}